import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { hasRole, useApi, useApp } from "../context/AppContext";
import { HARDWARE_LABEL, ISSUE_TEXT, PLATFORM_LABEL, STATUS_STYLE, ago, driftOf, platformOf } from "../lib/fleet";
import { PurchasesPage } from "./PurchasesPage";
import { DeviceDetailsDrawer } from "./DevicesPage";
import DeviceConsolePage from "./DeviceConsolePage";
import { LayoutTab } from "./cabinet/LayoutTab";
import { TrainingTab } from "./cabinet/TrainingTab";
import { WeightTab } from "./cabinet/WeightTab";
import { LiveTest } from "./cabinet/LiveTest";
import { CabinetSettings, GalleryRollback } from "./cabinet/CabinetAdmin";
import { ReleasesTab } from "./cabinet/ReleasesTab";

// One cabinet (docs/tasks/35, level C). Overview reads the cabinet's health;
// the other tabs are the existing pages, embedded unchanged until each is
// rebuilt over the cabinet's own data (phases 3-6).

const TABS = [
  { id: "overview", label: "Overview", roles: [] },
  { id: "models", label: "Models", roles: [] },
  { id: "layout", label: "Layout & stock", roles: [] },
  { id: "training", label: "Training", roles: [] },
  { id: "weight", label: "Weight", roles: [], when: (ctx) => ctx.weight || ctx.type?.weighed_trays },
  { id: "live", label: "Live & test", roles: ["tenant_admin", "device_operator"] },
  { id: "purchases", label: "Purchases", roles: ["tenant_admin", "auditor"] },
  { id: "details", label: "Details & log", roles: ["tenant_admin", "device_operator"] },
  { id: "releases", label: "Releases", roles: ["tenant_admin", "ml_operator", "device_operator", "auditor"] },
  { id: "console", label: "Console", roles: ["tenant_admin", "device_operator"] },
];

function gb(bytes) {
  return bytes == null ? "—" : `${(bytes / 1e9).toFixed(1)} GB`;
}

function Row({ k, children }) {
  return (<><dt className="text-slate-500">{k}</dt><dd>{children}</dd></>);
}

function TypeAssignment({ deviceId, type, onChanged }) {
  const api = useApi();
  const { user } = useApp();
  const [types, setTypes] = useState([]);
  const [pick, setPick] = useState("");
  const [err, setErr] = useState(null);
  const may = hasRole(user, "tenant_admin", "device_operator");
  useEffect(() => { api.listCabinetTypes().then(setTypes).catch(() => {}); }, [api]);
  const save = async () => {
    setErr(null);
    try {
      await api.setDeviceCabinetType(deviceId, pick);
      onChanged();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };
  return (
    <div className="space-y-1">
      {type ? <Link className="text-sky-700 hover:underline" to={`/types/${type.id}`}>{type.name}</Link> : <span className="text-amber-700">none</span>}
      {may && (
        <div className="flex items-center gap-2">
          <select className="input py-1 text-xs" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">{type ? "change the type…" : "assign a type…"}</option>
            {types.filter((t) => t.id !== type?.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn btn-secondary py-1 text-xs" disabled={!pick} onClick={save}>Save</button>
        </div>
      )}
      {type && may && <p className="text-xs text-slate-500">Changing the type changes which training rows its next gallery is built from.</p>}
      {err && <p className="text-xs text-rose-700">{err}</p>}
    </div>
  );
}

const ROLE_LABEL = { gallery: "Gallery", encoder: "Encoder", detector: "Detector", face: "Face guard" };

function ModelsTab({ health, type, weight, shelfUpdate }) {
  const r = health?.report || {};
  const exp = type?.expected || {};
  const cab = { health, device: { hardware: health?.hardware } };
  const drift = type ? driftOf(cab, type) : [];
  const board = platformOf(cab);
  const eng = (exp.detector?.engines || {})[board];
  const up = shelfUpdate?.update;
  const fwExpected = exp.pico_fw;
  const fwActual = weight?.pico?.fw;
  return (
    <div className="space-y-4">
      <div className="card overflow-x-auto">
        <h2 className="mb-2 text-sm font-semibold">What the cabinet runs</h2>
        {!r.models ? (
          <p className="text-sm text-slate-500">Not reported: the runtime needs a release from 22 October or later. Until then only the gallery is known: <span className="font-mono">{health?.model?.reported || r.model_version || "—"}</span>.</p>
        ) : (
          <table className="table">
            <thead><tr><th>Model</th><th>Name</th><th>Version</th><th>Engine</th><th>Self-check</th><th>File</th><th>sha256</th><th>From</th><th>Loaded</th></tr></thead>
            <tbody>
              {r.models.map((m) => (
                <tr key={m.role}>
                  <td>{ROLE_LABEL[m.role] || m.role}</td>
                  <td className="text-xs">{m.name || "—"}</td>
                  <td className="font-mono text-xs">{m.version || "—"}</td>
                  <td className="text-xs">{m.engine || "—"}{m.loaded === false ? <span className="text-rose-700"> · not loaded</span> : null}</td>
                  <td className="text-xs">{m.self_check != null ? m.self_check : "—"}</td>
                  <td className="font-mono text-xs">{m.file || "—"}</td>
                  <td className="font-mono text-xs">{m.file_sha256 || m.sha256 || "—"}</td>
                  <td className="text-xs">{m.source || "—"}</td>
                  <td className="text-xs">{m.loaded_at ? ago(m.loaded_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {r.review_window_mode && <p className="mt-2 text-xs text-slate-600">Review windows: <strong>{r.review_window_mode}</strong></p>}
      </div>

      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">What its type expects</h2>
        {!type ? <p className="text-sm text-amber-700">The cabinet has no type, so nothing is expected of it.</p>
          : !Object.keys(exp).length ? <p className="text-sm text-slate-500">The type <Link className="text-sky-700 hover:underline" to={`/types/${type.id}`}>{type.name}</Link> does not say yet.</p> : (
            <dl className="grid grid-cols-[12rem_1fr] gap-y-1 text-sm">
              <dt className="text-slate-500">Platform</dt><dd>{PLATFORM_LABEL[type.platform] || type.platform || "not set"}</dd>
              <dt className="text-slate-500">Encoder</dt><dd className="font-mono text-xs">{exp.encoder_version || "—"}</dd>
              <dt className="text-slate-500">Detector</dt><dd className="text-xs">{exp.detector ? `${exp.detector.name || "?"} ${exp.detector.version || ""}${eng ? ` · ${eng.kind} ${(eng.sha256 || eng.hef_sha256 || "").slice(0, 12)}` : ""}` : "—"}</dd>
              <dt className="text-slate-500">Window mode</dt><dd className="text-xs">{exp.review_window_mode || "—"}</dd>
              <dt className="text-slate-500">Sensor board firmware</dt><dd className="text-xs">{fwExpected || "—"}{fwExpected && fwActual ? (fwActual === fwExpected ? " · matches" : <span className="text-amber-700"> · runs {fwActual}</span>) : ""}</dd>
            </dl>
          )}
        {drift.length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">{drift.map((d, i) => <li key={i}>{d.what}: runs {d.actual}, expects {d.expected}</li>)}</ul>
        )}
        {type && Object.keys(exp).length > 0 && drift.length === 0 && r.models && <p className="mt-2 text-sm text-emerald-700">Matches its type.</p>}
      </div>

      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">What the cloud sent</h2>
        <dl className="grid grid-cols-[12rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-500">Assigned gallery</dt><dd className="font-mono text-xs">{health?.model?.desired || "—"}</dd>
          <dt className="text-slate-500">Reported gallery</dt><dd className="font-mono text-xs">{health?.model?.reported || "—"} <GalleryRollback deviceId={health?.device_id} health={health} /></dd>
          <dt className="text-slate-500">Last shelf update</dt>
          <dd className="text-xs">
            {up ? `${up.status}${up.phase ? ` · ${up.phase}` : ""}${up.deployment_status ? ` · ${up.deployment_status}` : ""} · gallery ${up.gallery_version || "—"}${up.installed ? " · the cabinet runs it" : ""}` : "none"}
            {shelfUpdate?.next ? " · another waits behind it" : ""}
          </dd>
        </dl>
        <p className="mt-2 text-xs text-slate-500">The detector and the encoder's engine file reach a cabinet outside the signed bundle today (an environment file), so only the cabinet's own report says which it runs.</p>
      </div>
    </div>
  );
}

function Overview({ deviceId, health, type, temperature, weight, onTypeChanged }) {
  const r = health?.report || {};
  const sys = r.system || {};
  const door = r.door || {};
  const cams = r.cameras || {};
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">Fit to sell?</h2>
        <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
          <Row k="Status"><span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[health?.status] || STATUS_STYLE.unknown}`}>{health?.status || "unknown"}</span></Row>
          {health?.service?.active && (
            <Row k="In service">
              <span className="font-medium text-rose-700">purchases refused since {ago(health.service.since)}</span>
              {health.service.reason ? <span className="text-rose-700"> · {health.service.reason}</span> : null}
              {(health.service.issues || []).length ? <span className="text-slate-600"> · {health.service.issues.map((c) => ISSUE_TEXT[c] || c).join("; ")}</span> : null}
            </Row>
          )}
          <Row k="Issues">{(health?.issues || []).map((c) => ISSUE_TEXT[c] || c).join("; ") || "none"}</Row>
          <Row k="Warnings">{(health?.warnings || []).map((c) => ISSUE_TEXT[c] || c).join("; ") || "none"}</Row>
          <Row k="Last seen">{ago(health?.last_seen_at)}{health?.stale ? " (report stale)" : ""}</Row>
          <Row k="Recognition">{r.recognition_enabled ? "on" : "off"} · analysis {r.analysis_mode || "—"}</Row>
          <Row k="Gallery">
            <span className="font-mono text-xs">{health?.model?.reported || r.model_version || "—"}</span>
            {r.model_error ? <span className="text-rose-700"> · {r.model_error}</span> : null}
          </Row>
          <Row k="Cameras"><span className={cams.expected > 0 && cams.healthy < cams.expected ? "font-medium text-rose-700" : ""}>{cams.expected != null ? `${cams.healthy}/${cams.expected} healthy` : "—"}</span>{cams.error ? <span className="text-rose-700"> · {cams.error}</span> : null}</Row>
          <Row k="Session">{r.session ? `${r.session.state} (${String(r.session.id).slice(0, 8)})` : "none"}</Row>
        </dl>
      </div>
      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">Cabinet</h2>
        <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
          <Row k="Type"><TypeAssignment deviceId={deviceId} type={type} onChanged={onTypeChanged} /></Row>
          <Row k="Board">{HARDWARE_LABEL[health?.hardware] || health?.hardware || "—"} · {sys.hostname || "—"}</Row>
          <Row k="Door">{door.state || "—"} · {door.locked ? "locked" : "unlocked"} · {door.signal === "sensor" ? "contact sensor" : door.signal || "—"} · {door.lock_backend || "—"} {door.mode ? `(${door.mode})` : ""}</Row>
          <Row k="Opens a session">{door.auto_session ? "when the door opens" : "only on a command"}</Row>
          {door.layout === "pair" && (
            <Row k="Two locks">
              {Object.entries(door.locks || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"} · coils {door.relay_on ? "energised" : "at rest"}
            </Row>
          )}
          {door.charge && (
            <Row k="Tablet charging">
              <span className={door.charge.relay_stuck ? "text-rose-700" : ""}>
                {door.charge.on ? "on" : "off"}{door.charge.operator_off ? " (switched off by an operator)" : ""}
                {door.charge.relay_stuck ? " · relay stuck" : ""}{door.charge.witness ? ` · witness ${door.charge.witness}` : ""}
              </span>
            </Row>
          )}
          <Row k="Temperature">
            {temperature?.has_sensor ? `${temperature.celsius?.toFixed(1)} °C` : "no sensor"}
            {temperature?.out_of_range ? <span className="text-rose-700"> · out of {temperature.limits?.min}–{temperature.limits?.max} °C</span> : null}
            {temperature?.stale ? " (stale)" : ""}
          </Row>
          <Row k="Weight board">
            {weight === undefined ? "…" : weight === null ? "none reported"
              : `${weight.pico_connected ? "connected" : "not connected"} · ${weight.pico?.device_id || "—"} · firmware ${weight.pico?.fw || "—"} · ${(weight.channels || []).length} channels · ${(weight.planogram?.trays || []).length} trays${weight.stale ? " (stale)" : ""}`}
          </Row>
        </dl>
      </div>
      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">System</h2>
        <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
          <Row k="Code release"><span className="font-mono text-xs">{sys.code_version || "—"}</span></Row>
          <Row k="Uptime">{sys.uptime_s ? `${(sys.uptime_s / 3600).toFixed(1)} h` : "—"}</Row>
          <Row k="CPU">{sys.cpu_temp_c != null ? `${sys.cpu_temp_c} °C` : "—"} · load {(sys.load_avg || []).map((x) => x.toFixed(1)).join(" ") || "—"}</Row>
          <Row k="Memory">{sys.memory ? `${sys.memory.used_pct}% used of ${gb(sys.memory.total_bytes)}` : "—"}</Row>
          <Row k="Disk">{sys.disk ? <span className={sys.disk.free_pct < 10 ? "text-amber-700" : ""}>{gb(sys.disk.free_bytes)} free of {gb(sys.disk.total_bytes)} ({sys.disk.free_pct}%)</span> : "—"}</Row>
          <Row k="Network">{health?.ip_address || "—"}{health?.location ? ` · ${health.location}` : ""}</Row>
          <Row k="Features">{(r.features || []).join(", ") || "—"}</Row>
        </dl>
      </div>
      {!r.models && (
        <div className="card text-xs text-slate-500">
          This cabinet's runtime does not report its models, lock pair, tablet charging or window mode yet: it needs a release from 22 October or
          later (docs/tasks/35, gaps D1 and D2).
        </div>
      )}
    </div>
  );
}

export function CabinetPage() {
  const { deviceId } = useParams();
  const api = useApi();
  const { user } = useApp();
  const [params, setParams] = useSearchParams();
  const [health, setHealth] = useState(null);
  const [type, setType] = useState(null);
  const [temperature, setTemperature] = useState(null);
  const [weight, setWeight] = useState(undefined);
  const [shelfUpdate, setShelfUpdate] = useState(null);
  const [err, setErr] = useState(null);

  const tabs = TABS.filter((t) => hasRole(user, ...t.roles) && (!t.when || t.when({ weight, type })));
  const tab = tabs.find((t) => t.id === params.get("tab"))?.id || "overview";

  const loadType = () => api.getDeviceCabinetType(deviceId).then((r) => setType(r?.cabinet_type || null)).catch(() => setType(null));
  useEffect(() => {
    setErr(null);
    api.getDeviceHealth(deviceId).then(setHealth).catch((e) => setErr(String(e.message || e)));
    void loadType();
    api.getDeviceCabinetTemperature(deviceId).then(setTemperature).catch(() => setTemperature(null));
    api.getDeviceWeight(deviceId).then(setWeight).catch(() => setWeight(null));
    api.getDeviceShelfUpdate(deviceId).then(setShelfUpdate).catch(() => setShelfUpdate(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId]);

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500">
          <Link className="hover:underline" to="/">Fleet</Link>
          {" › "}
          {type ? <Link className="hover:underline" to={`/types/${type.id}`}>{type.name}</Link> : "no type"}
          {" › "}
          {health?.name || deviceId}
        </div>
        <h1 className="text-2xl font-semibold">{health?.name || "Cabinet"}</h1>
        <p className="font-mono text-xs text-slate-500">{deviceId}</p>
      </div>
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map((t) => (
          <button key={t.id} type="button"
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === t.id ? "border-sky-600 font-medium text-sky-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}
            onClick={() => setParams(t.id === "overview" ? {} : { tab: t.id })}>
            {t.label}
          </button>
        ))}
      </div>
      {err && <p className="text-sm text-rose-700">{err}</p>}
      {tab === "overview" && (
        <div className="space-y-4">
          <Overview deviceId={deviceId} health={health} type={type} temperature={temperature} weight={weight} onTypeChanged={loadType} />
          <CabinetSettings deviceId={deviceId} health={health} onChanged={() => api.getDeviceHealth(deviceId).then(setHealth).catch(() => {})} />
        </div>
      )}
      {tab === "models" && <ModelsTab health={health} type={type} weight={weight} shelfUpdate={shelfUpdate} />}
      {tab === "layout" && <LayoutTab deviceId={deviceId} />}
      {tab === "training" && <TrainingTab deviceId={deviceId} />}
      {tab === "weight" && <WeightTab deviceId={deviceId} health={health} type={type} />}
      {tab === "live" && <LiveTest deviceId={deviceId} />}
      {tab === "purchases" && <PurchasesPage fixedDeviceId={deviceId} />}
      {tab === "details" && <DeviceDetailsDrawer deviceId={deviceId} inline onClose={() => setParams({})} />}
      {tab === "releases" && <ReleasesTab deviceId={deviceId} health={health} />}
      {tab === "console" && <DeviceConsolePage deviceId={deviceId} />}
    </div>
  );
}
